import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatPrice, formatApiError } from "../../lib/api";

const STATUS_OPTIONS = ["pending", "paid", "processing", "shipped", "delivered", "cancelled"];

export default function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    const fetchAll = () => {
        setLoading(true);
        const params = filter === "all" ? {} : { status_filter: filter };
        api.get("/admin/orders", { params })
            .then(({ data }) => setOrders(data.orders))
            .finally(() => setLoading(false));
    };

    useEffect(fetchAll, [filter]);

    const updateStatus = async (id, status, tracking = null) => {
        try {
            await api.put(`/admin/orders/${id}`, { status, tracking_number: tracking });
            toast.success("Order updated");
            fetchAll();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div data-testid="admin-orders">
            <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Atelier</p>
            <h1 className="mt-2 font-display text-4xl text-white">Orders</h1>

            <div className="mt-6 flex flex-wrap gap-2">
                {["all", ...STATUS_OPTIONS].map((s) => (
                    <button
                        key={s}
                        onClick={() => setFilter(s)}
                        data-testid={`filter-status-${s}`}
                        className={`border px-4 py-2 text-[10px] uppercase tracking-widest ${
                            filter === s ? "border-gold bg-gold text-obsidian" : "border-border-dim text-white/70 hover:border-gold"
                        }`}
                    >
                        {s}
                    </button>
                ))}
            </div>

            <div className="mt-8 space-y-4">
                {loading ? (
                    <p className="text-white/40">Loading…</p>
                ) : orders.length === 0 ? (
                    <p className="py-10 text-center text-white/40">No orders match.</p>
                ) : (
                    orders.map((o) => (
                        <div key={o.id} className="border border-border-dim bg-surface p-6" data-testid={`order-${o.id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                                        {o.id.slice(0, 8)} · {(o.created_at || "").slice(0, 10)}
                                    </p>
                                    <p className="mt-1 font-display text-xl text-white">{o.user_email}</p>
                                    <p className="text-xs text-white/40">
                                        {o.items.length} pieces · {formatPrice(o.total)}
                                    </p>
                                </div>
                                <div className="flex flex-wrap items-center gap-3">
                                    <select
                                        value={o.status}
                                        onChange={(e) => updateStatus(o.id, e.target.value, o.tracking_number)}
                                        data-testid={`status-select-${o.id}`}
                                        className="border border-border-dim bg-transparent px-3 py-2 text-xs uppercase tracking-widest text-white outline-none focus:border-gold"
                                    >
                                        {STATUS_OPTIONS.map((s) => (
                                            <option key={s} value={s} className="bg-obsidian">
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        placeholder="Tracking #"
                                        defaultValue={o.tracking_number || ""}
                                        onBlur={(e) => {
                                            if (e.target.value !== (o.tracking_number || "")) {
                                                updateStatus(o.id, o.status, e.target.value);
                                            }
                                        }}
                                        data-testid={`tracking-${o.id}`}
                                        className="border border-border-dim bg-transparent px-3 py-2 text-xs text-white outline-none focus:border-gold"
                                    />
                                </div>
                            </div>

                            <details className="mt-4">
                                <summary className="cursor-pointer text-xs uppercase tracking-widest text-white/60 hover:text-gold">
                                    View items
                                </summary>
                                <div className="mt-4 space-y-2">
                                    {o.items.map((it, i) => (
                                        <div key={i} className="flex gap-3 text-sm">
                                            <img src={it.product?.images?.[0]} alt="" className="h-14 w-12 object-cover" />
                                            <div className="flex-1">
                                                <p className="text-white">{it.product?.name}</p>
                                                <p className="text-xs text-white/40">
                                                    {[it.size, it.color].filter(Boolean).join(" / ")} × {it.quantity}
                                                </p>
                                            </div>
                                            <p className="text-gold">{formatPrice(it.line_total)}</p>
                                        </div>
                                    ))}
                                </div>
                            </details>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
